<template>
  <h5 class="title is-5">FAQ</h5>
  <div class="frame-parent">
    <div class="faq-parent">
      <div
        v-for="content in faqData"
        :key="content.content"
        :id="content.content"
        ref="faqSections"
        class="faq-section"
      >
        <h5 class="title is-5">
          {{ content.content }}
          <a :href="'#' + content.content" class="anchor-link">🔗</a>
        </h5>
        <FAQItem
          v-for="question in content.questions"
          :key="question.question"
          :title="question.question"
          :details="question.details"
          :image="question.image"
        />
        <div v-if="content.link" class="faq-link">
          <a :href="content.link.url" target="_blank">
            {{ content.link.text }}
          </a>
        </div>
      </div>
    </div>
    <div class="menu-parent">
      <FAQSection
        v-for="(title, index) in faqTitles"
        :key="index"
        :title="title"
      />
    </div>
  </div>
</template>

<script>
import { onBeforeUnmount, onMounted, ref } from "vue";
import FAQItem from "./components/Item.vue";
import FAQSection from "./components/Section.vue";
import faqDataEN from "./faq-data-en-US.json";
import faqDataJA from "./faq-data-ja.json";

import { useI18n } from "vue-i18n";

export default {
  components: {
    FAQItem,
    FAQSection,
  },
  setup() {
    const faqSections = ref([]);
    const observer = ref(null);
    let currentActiveId = ref("");
    const faqData = ref([]);
    const { locale } = useI18n();
    if (locale.value === "ja") {
      faqData.value = faqDataJA;
    } else {
      faqData.value = faqDataEN;
    }
    const faqTitles = faqData.value.map((item) => item.content);

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const id = entry.target.id;
          if (id !== currentActiveId.value) {
            history.replaceState(null, null, `#${id}`);
            currentActiveId.value = id;
          }
        }
      });
    };

    const scrollToAnchor = (event) => {
      event.preventDefault();
      const targetId = event.target.getAttribute("href").substring(1);
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    onMounted(() => {
      // IntersectionObserverの初期化
      observer.value = new IntersectionObserver(handleIntersection, {
        rootMargin: "0px 0px -90% 0px",
        threshold: 0.1,
      });

      faqSections.value = document.querySelectorAll(".faq-section");
      faqSections.value.forEach((section) => {
        observer.value.observe(section);
      });

      const anchorLinks = document.querySelectorAll(".anchor-link");
      anchorLinks.forEach((link) => {
        link.addEventListener("click", scrollToAnchor);
      });

      const hash = decodeURIComponent(window.location.hash.substring(1)); // #を取り除いてデコード
      if (hash) {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    });

    onBeforeUnmount(() => {
      if (observer.value) {
        observer.value.disconnect();
      }

      const anchorLinks = document.querySelectorAll(".anchor-link");
      anchorLinks.forEach((link) => {
        link.removeEventListener("click", scrollToAnchor);
      });
    });

    return {
      faqData,
      faqSections,
      faqTitles,
    };
  },
};
</script>

<style scoped>
.faq-parent {
  padding-right: 20px;
  width: 700px;
}

.menu-parent {
  width: 400px; /* Fixed width for the menu */
  position: fixed;
  margin-left: 700px;
  top: 200px;
}

.frame-parent {
  width: 100%;
  position: relative;
  background-color: #fff;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 10px;
  box-sizing: border-box;
  gap: 10px;
  text-align: left;
  font-size: 16px;
  color: #363636;
  font-family: Arial;
}

.anchor-link {
  margin-left: 10px;
  color: #007bff;
  text-decoration: none;
}

.anchor-link:hover {
  text-decoration: underline;
}

.faq-section {
  margin-bottom: 20px;
}

/* スマホ用スタイル */
@media (max-width: 667px) {
  .faq-parent {
    width: 100%; /* スマホでは全幅を使用 */
    padding: 0 10px;
  }

  .menu-parent {
    display: none; /* スマホではメニューを非表示 */
  }

  .frame-parent {
    flex-direction: column; /* スマホでは縦に配置 */
    align-items: stretch;
    gap: 20px;
  }

  .anchor-link {
    font-size: 14px; /* スマホでリンクを小さく */
  }

  h5.title.is-5 {
    font-size: 18px; /* タイトルサイズを調整 */
  }
}
</style>
